import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { postForm, postJson, errorMessage } from '~/lib/http';
import type { ApiResult, ChatMessage, SubmissionInput, SubmitResult } from '~/lib/types';
import { emptySubmission } from '~/lib/types';
import { EventForm, validateSubmission } from '~/components/event-form';
import { Turnstile } from '~/components/turnstile';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { MicButton } from '~/components/mic-button';

type AiTab = 'text' | 'url' | 'image';

interface Draft {
  input: SubmissionInput;
  confirmed: boolean;
}

/**
 * The full "list your event" flow, extracted so it renders identically on the
 * /submit route AND inside the desktop modal. AI capture (paste text / paste
 * link / take-photo or upload flyer → AI fills the form) → editable drafts →
 * contact + submit. When `turnstileSiteKey` isn't supplied (modal), it fetches
 * /api/config on mount.
 */
export function SubmitFlow({
  turnstileSiteKey: initialKey,
  showMasthead = true,
  onSubmitted,
}: {
  turnstileSiteKey?: string;
  showMasthead?: boolean;
  onSubmitted?: () => void;
}) {
  const [turnstileSiteKey, setTurnstileSiteKey] = useState(initialKey ?? '');

  // If no key was passed in (modal path), fetch runtime config client-side.
  useEffect(() => {
    if (initialKey !== undefined) return;
    let alive = true;
    fetch('/api/config', { credentials: 'include' })
      .then((r) => r.json() as Promise<{ ok: boolean; data?: { turnstileSiteKey?: string } }>)
      .then((b) => {
        if (alive && b?.ok) setTurnstileSiteKey(b.data?.turnstileSiteKey ?? '');
      })
      .catch(() => {
        /* proceed without Turnstile */
      });
    return () => {
      alive = false;
    };
  }, [initialKey]);

  const [drafts, setDrafts] = useState<Draft[]>([{ input: emptySubmission(), confirmed: false }]);
  const [aiTab, setAiTab] = useState<AiTab>('text');
  const [aiText, setAiText] = useState('');
  const [aiUrl, setAiUrl] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiFound, setAiFound] = useState<number | null>(null);
  const uploadRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);
  const [result, setResult] = useState<SubmitResult | null>(null);

  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatTarget, setChatTarget] = useState(0);
  const [chatComplete, setChatComplete] = useState(false);

  const formsRef = useRef<HTMLDivElement>(null);

  function applyCaptured(events: SubmissionInput[]) {
    setDrafts(
      events.map((input) => ({ input: { ...emptySubmission(), ...input }, confirmed: false }))
    );
    setAiFound(events.length);
    setAiError(null);
    setChatTarget(0);
    setTimeout(() => formsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  }

  async function runCapture() {
    setAiBusy(true);
    setAiError(null);
    setAiFound(null);
    let res: ApiResult<{ events: SubmissionInput[] }>;
    if (aiTab === 'image') {
      const file = cameraRef.current?.files?.[0] ?? uploadRef.current?.files?.[0];
      if (!file) {
        setAiError('Take a photo or choose a flyer image first.');
        setAiBusy(false);
        return;
      }
      const form = new FormData();
      form.set('kind', 'image');
      form.set('image', file);
      res = await postForm('/api/ai/capture', form);
    } else if (aiTab === 'url') {
      res = await postJson('/api/ai/capture', { kind: 'url', url: aiUrl });
    } else {
      res = await postJson('/api/ai/capture', { kind: 'text', text: aiText });
    }
    setAiBusy(false);
    if (res.ok && res.data.events.length > 0) {
      applyCaptured(res.data.events);
    } else {
      setAiError(
        "Couldn't pull the details out of that. No problem — the form below takes about 2 minutes by hand."
      );
    }
  }

  function updateDraft(i: number, input: SubmissionInput) {
    setDrafts((d) => d.map((draft, idx) => (idx === i ? { input, confirmed: false } : draft)));
  }

  function toggleConfirm(i: number) {
    setDrafts((d) =>
      d.map((draft, idx) => (idx === i ? { ...draft, confirmed: !draft.confirmed } : draft))
    );
  }

  function removeDraft(i: number) {
    setDrafts((d) => (d.length > 1 ? d.filter((_, idx) => idx !== i) : d));
    setChatTarget(0);
  }

  function addDraft() {
    setDrafts((d) => [...d, { input: emptySubmission(), confirmed: false }]);
  }

  async function sendChat(e: React.FormEvent) {
    e.preventDefault();
    const content = chatInput.trim();
    if (!content || chatBusy) return;
    const nextMessages: ChatMessage[] = [...chatMessages, { role: 'user', content }];
    setChatMessages(nextMessages);
    setChatInput('');
    setChatBusy(true);
    const res = await postJson<{ reply: string; draft: SubmissionInput; complete: boolean }>(
      '/api/ai/chat',
      { messages: nextMessages, draft: drafts[chatTarget]?.input ?? emptySubmission() }
    );
    setChatBusy(false);
    if (res.ok) {
      setChatMessages([...nextMessages, { role: 'assistant', content: res.data.reply }]);
      updateDraft(chatTarget, { ...emptySubmission(), ...res.data.draft });
      setChatComplete(res.data.complete);
    } else {
      setChatMessages([
        ...nextMessages,
        {
          role: 'assistant',
          content: "I hit a snag on my end — keep filling the form directly and you'll be fine.",
        },
      ]);
    }
  }

  async function submitAll(e: React.FormEvent) {
    e.preventDefault();
    const errors: string[] = [];
    drafts.forEach((d, i) => {
      for (const err of validateSubmission(d.input)) {
        errors.push(drafts.length > 1 ? `Event ${i + 1}: ${err}` : err);
      }
    });
    if (!contactName.trim()) errors.push('Your name is required.');
    if (!contactEmail.trim())
      errors.push('Your email is required — it’s how we send your edit link.');
    if (errors.length > 0) {
      setSubmitErrors(errors);
      return;
    }
    setSubmitErrors([]);
    setSubmitting(true);
    const res = await postJson<SubmitResult>('/api/events', {
      events: drafts.map((d) => d.input),
      contactName,
      contactEmail,
      website: honeypot,
      ...(turnstileToken ? { turnstileToken } : {}),
    });
    setSubmitting(false);
    if (res.ok) {
      setResult(res.data);
      onSubmitted?.();
    } else {
      setSubmitErrors([errorMessage(res.error)]);
    }
  }

  function resetForAnother() {
    setDrafts([{ input: emptySubmission(), confirmed: false }]);
    setResult(null);
    setAiFound(null);
    setAiText('');
    setAiUrl('');
    setChatMessages([]);
    setChatComplete(false);
  }

  // ── Success screen ─────────────────────────────────────
  if (result) {
    return (
      <div className="card card-pad-lg stack success-screen">
        <div className="success-check" aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24">
            <path
              d="M4 12.5l5 5L20 6.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div>
          <h2>In review — usually live within 24 hours.</h2>
          <p className="muted">
            We check every listing to keep the calendar clean. You'll get an email when{' '}
            {result.submitted === 1 ? 'it goes' : 'they go'} live.
          </p>
        </div>

        <div>
          <h3 className="detail-section-head">Your edit links — save these</h3>
          <p className="small muted">
            Each link lets you update or cancel that listing anytime. No account, no password — the
            link is the key.
          </p>
          <ul className="edit-link-list">
            {result.results.map((r) => (
              <li key={r.editUrl}>
                <strong>{r.title}</strong>
                <a href={r.editUrl} className="small t-num">
                  {r.editUrl}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="row">
          <button type="button" className="ui-btn btn-primary" onClick={resetForAnother}>
            List another event
          </button>
          <Link to="/" className="ui-btn btn-secondary">
            Back to the calendar
          </Link>
        </div>

        <DigestOptIn defaultEmail={contactEmail} />
      </div>
    );
  }

  // ── The submit flow ────────────────────────────────────
  return (
    <>
      {showMasthead && (
        <section className="masthead">
          <h1>List your event in under 2 minutes — free</h1>
          <p className="masthead-tagline">
            No account. No fee. Every chamber mixer, BNI seat, and meetup in the Bay belongs here —
            including yours.
          </p>
        </section>
      )}

      {/* Section A — AI capture */}
      <section className="card stack" aria-labelledby="ai-capture-head">
        <div>
          <h2 id="ai-capture-head">Let AI do the typing</h2>
          <p className="muted small">
            Paste your event description, drop a link, or snap the flyer — we'll fill the form for
            you. Multiple events in one paste? We'll catch them all.
          </p>
        </div>

        <div role="tablist" className="tabs" aria-label="How do you want to add it?">
          <button
            type="button"
            role="tab"
            id="ai-tab-text"
            aria-controls="ai-panel"
            aria-selected={aiTab === 'text' ? 'true' : 'false'}
            onClick={() => setAiTab('text')}
          >
            Paste text
          </button>
          <button
            type="button"
            role="tab"
            id="ai-tab-url"
            aria-controls="ai-panel"
            aria-selected={aiTab === 'url' ? 'true' : 'false'}
            onClick={() => setAiTab('url')}
          >
            Paste a link
          </button>
          <button
            type="button"
            role="tab"
            id="ai-tab-image"
            aria-controls="ai-panel"
            aria-selected={aiTab === 'image' ? 'true' : 'false'}
            onClick={() => setAiTab('image')}
          >
            Photo / flyer
          </button>
        </div>

        <div id="ai-panel" role="tabpanel" aria-labelledby={`ai-tab-${aiTab}`}>
          {aiTab === 'text' && (
            <div className="field">
              <Label htmlFor="ai-text">Event details, any format</Label>
              <textarea
                id="ai-text"
                className="ui-textarea"
                rows={5}
                placeholder="Paste the email blast, the Facebook post, your notes — anything with the who/when/where in it."
                value={aiText}
                onChange={(e) => setAiText(e.target.value)}
              />
              <div className="capture-row">
                <MicButton
                  title="Speak the event details"
                  onResult={(text) => setAiText((t) => (t ? `${t} ${text}` : text))}
                />
                <span className="field-hint">Or tap the mic and just say it out loud.</span>
              </div>
            </div>
          )}

          {aiTab === 'url' && (
            <div className="field">
              <Label htmlFor="ai-url">Event page link</Label>
              <Input
                id="ai-url"
                type="url"
                inputMode="url"
                placeholder="https://www.eventbrite.com/e/…"
                value={aiUrl}
                onChange={(e) => setAiUrl(e.target.value)}
              />
              <p className="field-hint">
                Eventbrite, Meetup, a chamber page — any public event page.
              </p>
            </div>
          )}

          {aiTab === 'image' && (
            <div className="field">
              <Label htmlFor="ai-image-upload">Flyer image</Label>
              <div className="capture-row">
                {/* Camera capture — opens the camera on phones/tablets */}
                <label className="ui-btn btn-secondary capture-btn" htmlFor="ai-image-camera">
                  Take a photo
                  <input
                    id="ai-image-camera"
                    ref={cameraRef}
                    className="sr-only"
                    type="file"
                    accept="image/*"
                    capture="environment"
                  />
                </label>
                {/* Standard upload — gallery / file picker */}
                <input
                  id="ai-image-upload"
                  ref={uploadRef}
                  className="ui-input"
                  type="file"
                  accept="image/*"
                />
              </div>
              <p className="field-hint">
                Snap the flyer, or upload a JPG/PNG of the flyer, screenshot, or postcard.
              </p>
            </div>
          )}
        </div>

        <div className="row">
          <button
            type="button"
            className="ui-btn btn-primary"
            onClick={runCapture}
            disabled={
              aiBusy ||
              (aiTab === 'text' ? !aiText.trim() : aiTab === 'url' ? !aiUrl.trim() : false)
            }
          >
            {aiBusy ? 'Reading…' : 'Extract event details'}
          </button>
          {aiFound !== null && (
            <p className="small notice-inline-success" role="status">
              Found {aiFound} event{aiFound === 1 ? '' : 's'} — review below, then submit.
            </p>
          )}
        </div>

        {aiError && (
          <div className="notice" role="status">
            {aiError}
          </div>
        )}
      </section>

      {/* Section B — the form(s) */}
      <div ref={formsRef} className="stack drafts-stack">
        {drafts.map((draft, i) => (
          <section
            key={i}
            className={
              draft.confirmed ? 'card stack draft-card is-confirmed' : 'card stack draft-card'
            }
            aria-label={drafts.length > 1 ? `Event ${i + 1}` : 'Event details'}
          >
            <div className="row-between">
              <h2>{drafts.length > 1 ? `Event ${i + 1}` : 'The details'}</h2>
              <div className="row">
                <button
                  type="button"
                  className={
                    draft.confirmed
                      ? 'ui-btn btn-sm confirm-btn is-on'
                      : 'ui-btn btn-secondary btn-sm confirm-btn'
                  }
                  aria-pressed={draft.confirmed ? 'true' : 'false'}
                  onClick={() => toggleConfirm(i)}
                >
                  {draft.confirmed ? 'Looks right ✓' : 'Looks right?'}
                </button>
                {drafts.length > 1 && (
                  <button
                    type="button"
                    className="ui-btn btn-danger-ghost btn-sm"
                    onClick={() => removeDraft(i)}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
            <EventForm
              value={draft.input}
              onChange={(v) => updateDraft(i, v)}
              idPrefix={`ev${i}`}
            />
          </section>
        ))}

        <div>
          <button type="button" className="ui-btn btn-ghost" onClick={addDraft}>
            + Add another event
          </button>
        </div>
      </div>

      {/* Assistant drawer */}
      <section className="assistant-section">
        <button
          type="button"
          className="ui-btn btn-secondary btn-sm"
          aria-expanded={chatOpen ? 'true' : 'false'}
          aria-controls="assistant-panel"
          onClick={() => setChatOpen((v) => !v)}
        >
          {chatOpen ? 'Hide the assistant' : 'Ask the assistant'}
        </button>
        {chatOpen && (
          <div id="assistant-panel" className="chat-panel">
            <div className="chat-log" aria-live="polite">
              {chatMessages.length === 0 && (
                <p className="chat-msg chat-msg-assistant">
                  Tell me about your event in plain words — "chamber breakfast next Tuesday at 8 at
                  the Vinoy, $15" — and I'll fill in the form for you.
                </p>
              )}
              {chatMessages.map((m, i) => (
                <p
                  key={i}
                  className={
                    m.role === 'user' ? 'chat-msg chat-msg-user' : 'chat-msg chat-msg-assistant'
                  }
                >
                  {m.content}
                </p>
              ))}
              {chatComplete && (
                <p className="chat-msg chat-msg-assistant">
                  <strong>That draft looks complete</strong> — give it a once-over above and hit
                  submit.
                </p>
              )}
            </div>
            {drafts.length > 1 && (
              <div className="chat-target-row">
                <label className="ui-label small" htmlFor="chat-target">
                  Working on
                </label>
                <select
                  id="chat-target"
                  className="ui-select"
                  value={chatTarget}
                  onChange={(e) => setChatTarget(Number(e.target.value))}
                >
                  {drafts.map((d, i) => (
                    <option key={i} value={i}>
                      Event {i + 1}
                      {d.input.title ? ` — ${d.input.title}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <form className="chat-input-row" onSubmit={sendChat}>
              <label className="sr-only" htmlFor="chat-input">
                Message the assistant
              </label>
              <Input
                id="chat-input"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Describe your event…"
              />
              <button type="submit" className="ui-btn btn-primary" disabled={chatBusy}>
                {chatBusy ? '…' : 'Send'}
              </button>
            </form>
          </div>
        )}
      </section>

      {/* Section C — contact + submit */}
      <form className="card stack contact-section" onSubmit={submitAll}>
        <div>
          <h2>Where do we send your edit link?</h2>
          <p className="muted small">
            One email when it's approved, with a private link to edit or cancel anytime. Never
            shared, never spammed.
          </p>
        </div>

        <div className="form-grid">
          <div className="field">
            <Label htmlFor="contact-name">Your name *</Label>
            <Input
              id="contact-name"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              autoComplete="name"
              required
            />
          </div>
          <div className="field">
            <Label htmlFor="contact-email">Your email *</Label>
            <Input
              id="contact-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>
        </div>

        {/* Honeypot — humans never see this */}
        <div className="hp-field" aria-hidden="true">
          <label htmlFor="website-hp">Website</label>
          <input
            id="website-hp"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        <Turnstile siteKey={turnstileSiteKey} onToken={setTurnstileToken} />

        {submitErrors.length > 0 && (
          <div className="notice notice-error" role="alert">
            <ul className="error-list">
              {submitErrors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="row">
          <button type="submit" className="ui-btn btn-primary btn-lg" disabled={submitting}>
            {submitting
              ? 'Submitting…'
              : drafts.length > 1
                ? `Submit ${drafts.length} events for review`
                : 'Submit for review — free'}
          </button>
          <p className="small muted submit-note">In review — usually live within 24 hours.</p>
        </div>
      </form>
    </>
  );
}

function DigestOptIn({ defaultEmail }: { defaultEmail: string }) {
  const [email, setEmail] = useState(defaultEmail);
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');

  async function optIn(e: React.FormEvent) {
    e.preventDefault();
    setState('busy');
    const res = await postJson('/api/subscribe', { email });
    setState(res.ok ? 'done' : 'idle');
  }

  if (state === 'done') {
    return (
      <p className="notice notice-success" role="status">
        You're on the Monday digest. One email a week, every event that matters.
      </p>
    );
  }

  return (
    <form className="notice digest-optin" onSubmit={optIn}>
      <div>
        <strong>Want the weekly digest?</strong>
        <p className="small muted digest-optin-copy">
          Every Monday: the week's networking events, in one email.
        </p>
        <div className="row">
          <label className="sr-only" htmlFor="optin-email">
            Email
          </label>
          <Input
            id="optin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button type="submit" className="ui-btn btn-secondary" disabled={state === 'busy'}>
            {state === 'busy' ? 'Joining…' : 'Count me in'}
          </button>
        </div>
      </div>
    </form>
  );
}
